import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchKnowledge, submitKnowledgeFeedback } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BookOpen, Search, ThumbsUp, ThumbsDown, Eye, ExternalLink, Bot, Plus, X, FileText, AlertTriangle, Wrench, Lightbulb, Tag, BarChart3, Save } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TYPES = [
  { id: '', label: 'All', icon: BookOpen, color: 'var(--nexus-text-secondary)' },
  { id: 'RUNBOOK', label: 'Runbooks', icon: FileText, color: '#06B6D4' },
  { id: 'TROUBLESHOOTING', label: 'Troubleshooting', icon: Wrench, color: '#F59E0B' },
  { id: 'KNOWN_ERROR', label: 'Known Errors', icon: AlertTriangle, color: '#EF4444' },
  { id: 'BEST_PRACTICE', label: 'Best Practices', icon: Lightbulb, color: '#10B981' },
];

export default function KnowledgePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [newArticle, setNewArticle] = useState({ title: '', content: '', article_type: 'TROUBLESHOOTING', tags: '' });
  const token = localStorage.getItem('nexus_token');
  const queryClient = useQueryClient();

  const { data: articles, isLoading } = useQuery({
    queryKey: ['knowledge', searchTerm, filterType, filterTag],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (filterType) params.set('article_type', filterType);
      if (filterTag) params.set('tag', filterTag);
      const { data } = await axios.get(`${BACKEND_URL}/api/knowledge?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    keepPreviousData: true,
  });

  const { data: kedb } = useQuery({
    queryKey: ['kedb'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/knowledge/kedb`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
  });

  const { data: stats } = useQuery({
    queryKey: ['knowledge-stats'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/knowledge/stats`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
    enabled: showStats,
  });

  const createMutation = useMutation({
    mutationFn: async (article) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/knowledge`, { ...article, tags: article.tags.split(',').map(t => t.trim()).filter(Boolean) }, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: () => { toast.success('Article created'); setShowCreate(false); setNewArticle({ title: '', content: '', article_type: 'TROUBLESHOOTING', tags: '' }); queryClient.invalidateQueries(['knowledge']); },
    onError: () => toast.error('Failed to create article'),
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, content }) => {
      const { data } = await axios.patch(`${BACKEND_URL}/api/knowledge/${id}`, { content }, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: () => { toast.success('Article updated'); setEditingId(null); queryClient.invalidateQueries(['knowledge']); },
  });

  const handleFeedback = async (articleId, helpful) => {
    try { await submitKnowledgeFeedback(articleId, helpful); toast.success(helpful ? 'Marked as helpful' : 'Feedback recorded'); } catch { toast.error('Failed'); }
  };

  // Merge KB articles with KEDB entries for KNOWN_ERROR tab
  const allArticles = filterType === 'KNOWN_ERROR'
    ? [...(articles || []), ...(kedb || []).map(k => ({ ...k, content: `**Workaround:** ${k.workaround}\n\n**Symptoms:** ${k.symptoms?.join(', ')}\n\n**Recommended Fix:** ${k.recommended_fix}`, views: k.linked_incidents || 0, helpful: k.pattern_strength || 0, not_helpful: 0, created_by_agent: 'NOVA' }))]
    : (articles || []);

  // Extract all tags for filter
  const allTags = [...new Set((articles || []).flatMap(a => a.tags || []))];

  return (
    <div data-testid="knowledge-page" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Knowledge Base</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">AI-generated + manual articles \u2022 {(articles || []).length} articles{kedb?.length ? ` + ${kedb.length} KEDB entries` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowStats(!showStats)} data-testid="toggle-stats"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-xs text-[var(--nexus-text-secondary)] hover:bg-white/[0.04]">
            <BarChart3 size={12} /> Stats
          </button>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="create-article"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0d3d4d] border border-cyan-500/30 text-[var(--nexus-accent)] text-xs font-semibold hover:bg-[#0f4a5c]">
            <Plus size={12} /> New Article
          </button>
        </div>
      </div>

      {/* Stats Dashboard */}
      {showStats && stats && (
        <div className="nexus-card p-4 mb-4 animate-nexus-slide-in" data-testid="kb-stats">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatBox label="Articles" value={stats.total_articles} color="var(--nexus-accent)" />
            <StatBox label="Total Views" value={stats.total_views} color="var(--nexus-primary-bright)" />
            <StatBox label="Helpful Rate" value={`${stats.helpful_rate}%`} color="var(--nexus-success)" />
            <StatBox label="Coverage" value={`${stats.coverage?.coverage_pct}%`} color="var(--nexus-warning)" />
            <StatBox label="KEDB Entries" value={kedb?.length || 0} color="var(--nexus-danger)" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><span className="text-[10px] text-[var(--nexus-text-muted)] uppercase">By Type</span>
              <div className="flex flex-wrap gap-1 mt-1">{Object.entries(stats.by_type || {}).map(([k,v]) => <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[var(--nexus-text-secondary)]">{k}: {v}</span>)}</div>
            </div>
            <div><span className="text-[10px] text-[var(--nexus-text-muted)] uppercase">Top Tags</span>
              <div className="flex flex-wrap gap-1 mt-1">{Object.entries(stats.top_tags || {}).slice(0, 8).map(([k,v]) => <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[var(--nexus-accent)]">{k} ({v})</span>)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Create Article Form */}
      {showCreate && (
        <div className="nexus-card p-4 mb-4 animate-nexus-slide-in" data-testid="create-form">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Create New Article</h2>
            <button onClick={() => setShowCreate(false)}><X size={14} className="text-[var(--nexus-text-muted)]" /></button>
          </div>
          <div className="space-y-3">
            <input type="text" value={newArticle.title} onChange={e => setNewArticle(p => ({...p, title: e.target.value}))} placeholder="Article title"
              className="w-full px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)]" />
            <div className="flex gap-2">
              {TYPES.slice(1).map(t => (
                <button key={t.id} onClick={() => setNewArticle(p => ({...p, article_type: t.id}))}
                  className={`px-2 py-1 rounded text-[10px] border ${newArticle.article_type === t.id ? 'bg-[#0d2847] border-blue-500/30 text-[var(--nexus-primary-bright)]' : 'border-[var(--nexus-border)] text-[var(--nexus-text-muted)]'}`}>{t.label}</button>
              ))}
            </div>
            <textarea value={newArticle.content} onChange={e => setNewArticle(p => ({...p, content: e.target.value}))} rows={8} placeholder="Article content (markdown supported)"
              className="w-full px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-xs font-mono text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none focus:border-[var(--nexus-accent)] resize-none" />
            <input type="text" value={newArticle.tags} onChange={e => setNewArticle(p => ({...p, tags: e.target.value}))} placeholder="Tags (comma separated): kubernetes, memory-leak, P1"
              className="w-full px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-xs text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none" />
            <button onClick={() => createMutation.mutate(newArticle)} disabled={!newArticle.title.trim() || !newArticle.content.trim()}
              className="px-4 py-2 rounded-lg bg-[var(--nexus-accent)] text-white text-xs font-medium disabled:opacity-50">Create Article</button>
          </div>
        </div>
      )}

      {/* Category Tabs */}
      <div className="flex items-center gap-1 mb-3">
        {TYPES.map(t => {
          const Icon = t.icon;
          const active = filterType === t.id;
          return (
            <button key={t.id} onClick={() => setFilterType(t.id)} data-testid={`tab-${t.id || 'all'}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium border transition-colors ${
                active ? 'bg-[#0d2847] border-blue-500/30 text-[var(--nexus-primary-bright)]' : 'bg-[var(--nexus-bg-elevated)] border-[var(--nexus-border)] text-[var(--nexus-text-muted)] hover:bg-white/[0.04]'
              }`}>
              <Icon size={11} style={{ color: active ? t.color : undefined }} /> {t.label}
            </button>
          );
        })}
        {/* Tag filters */}
        {allTags.length > 0 && (
          <>
            <div className="w-px h-4 bg-[var(--nexus-border)] mx-1" />
            {allTags.slice(0, 5).map(tag => (
              <button key={tag} onClick={() => setFilterTag(filterTag === tag ? '' : tag)}
                className={`px-2 py-1 rounded text-[9px] font-mono border ${filterTag === tag ? 'bg-[#0d3d4d] border-cyan-500/30 text-[var(--nexus-accent)]' : 'border-[var(--nexus-border)] text-[var(--nexus-text-muted)]'}`}>
                <Tag size={8} className="inline mr-0.5" />{tag}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] mb-4">
        <Search size={16} className="text-[var(--nexus-text-muted)]" />
        <input data-testid="knowledge-search" type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search articles, tags, content..." className="bg-transparent text-sm text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none flex-1" />
        {searchTerm && <button onClick={() => setSearchTerm('')} className="text-[10px] text-[var(--nexus-accent)] hover:underline">Clear</button>}
      </div>

      {/* Articles */}
      {isLoading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-28 rounded-xl bg-[var(--nexus-bg-elevated)] animate-pulse" />)}</div>
      ) : (
        <div className="space-y-3" data-testid="knowledge-articles">
          {allArticles.map((article) => {
            const typeConf = TYPES.find(t => t.id === article.article_type) || TYPES[0];
            const TypeIcon = typeConf.icon;
            const isExpanded = expandedId === article.id;
            const isEditing = editingId === article.id;

            return (
              <div key={article.id} className="nexus-card relative overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <TypeIcon size={12} style={{ color: typeConf.color }} />
                        <span className="text-[9px] uppercase tracking-wider font-medium" style={{ color: typeConf.color }}>{article.article_type || 'ARTICLE'}</span>
                      </div>
                      <button onClick={() => { setExpandedId(isExpanded ? null : article.id); setEditingId(null); }} className="text-left">
                        <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] hover:text-[var(--nexus-accent)] transition-colors">{article.title}</h3>
                      </button>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <div className="flex items-center gap-1"><Eye size={10} className="text-[var(--nexus-text-muted)]" /><span className="font-mono text-[10px] text-[var(--nexus-text-muted)] tabular-nums">{article.views} views</span></div>
                        {article.source_ticket_id && <div className="flex items-center gap-1"><ExternalLink size={10} className="text-[var(--nexus-text-muted)]" /><span className="font-mono text-[10px] text-[var(--nexus-primary-bright)]">{article.source_ticket_id}</span></div>}
                        {article.created_by_agent && <div className="flex items-center gap-1"><Bot size={10} className="text-[var(--nexus-accent)]" /><span className="font-mono text-[10px] text-[var(--nexus-accent)]">{article.created_by_agent}</span></div>}
                        {(article.tags || []).map(tag => <span key={tag} className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[var(--nexus-text-muted)]">{tag}</span>)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button onClick={() => handleFeedback(article.id, true)} className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#0d3d1d] text-green-300 hover:bg-green-500/20"><ThumbsUp size={11} /><span className="font-mono text-[10px] tabular-nums">{article.helpful}</span></button>
                      <button onClick={() => handleFeedback(article.id, false)} className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#4a1515] text-red-300 hover:bg-red-500/20"><ThumbsDown size={11} /><span className="font-mono text-[10px] tabular-nums">{article.not_helpful}</span></button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-3 animate-nexus-slide-in">
                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={12}
                            className="w-full px-3 py-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-xs font-mono text-[var(--nexus-text-primary)] outline-none resize-none" />
                          <div className="flex gap-2">
                            <button onClick={() => editMutation.mutate({ id: article.id, content: editContent })}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0d3d1d] text-green-300 text-[10px] border border-green-500/30"><Save size={10} /> Save</button>
                            <button onClick={() => setEditingId(null)} className="px-3 py-1.5 rounded-lg bg-[var(--nexus-bg-base)] text-[var(--nexus-text-muted)] text-[10px] border border-[var(--nexus-border)]">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                          <div className="prose prose-invert prose-xs max-w-none text-xs text-[var(--nexus-text-secondary)] leading-relaxed [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-[var(--nexus-text-primary)] [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:text-[var(--nexus-text-primary)] [&_strong]:text-[var(--nexus-text-primary)] [&_code]:text-[var(--nexus-accent)] [&_code]:bg-[var(--nexus-bg-elevated)] [&_code]:px-1 [&_code]:rounded [&_li]:ml-4 [&_ol]:list-decimal [&_ul]:list-disc">
                            <ReactMarkdown>{article.content || ''}</ReactMarkdown>
                          </div>
                          <button onClick={() => { setEditingId(article.id); setEditContent(article.content || ''); }}
                            className="mt-2 text-[10px] text-[var(--nexus-accent)] hover:underline">Edit article</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {allArticles.length === 0 && (
            <div className="nexus-card p-12 text-center">
              <BookOpen size={32} className="text-[var(--nexus-text-muted)] mx-auto mb-3" />
              <p className="text-sm text-[var(--nexus-text-muted)]">{searchTerm ? 'No articles match your search' : 'No knowledge articles yet'}</p>
              <p className="text-[10px] text-[var(--nexus-text-muted)] mt-1">Trigger "Knowledge: Auto-Generate" from Settings to have LEARN-X create articles</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const StatBox = ({ label, value, color }) => (
  <div className="p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
    <p className="text-[9px] text-[var(--nexus-text-muted)] uppercase tracking-wider">{label}</p>
    <p className="font-heading text-lg font-semibold tabular-nums" style={{ color }}>{value}</p>
  </div>
);
